import axios, { AxiosRequestConfig } from 'axios';
import { inject, injectable } from "inversify";
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { HttpRequestBody, HttpRequestOptions } from "../types/http.types";
import { ApiErrorSink, reportApiError } from '../utils/api-error-report';
import { requestIdHeader } from '../utils/request-id';
import { readRetryAfterSeconds } from '../utils/retry-after';
import { ClientError, ServiceError, UnknownHTTPError } from '../utils/request.utils';
import { ApiConfig } from './api-config';
import { DOMStorage } from './dom-storage.service';
import { EnvironmentService } from './environment.service';
import { HttpProgressEvent, UNAUTHENTICATED_URLS } from './light-http.service';
import { ILightHttpService } from "./service-interfaces";

import { get } from '../utils/lodash-min';
/***
 * Try removing LightHttp after implementing this
 */
@injectable()
export class HttpClient implements ILightHttpService {
    private static wsDataProvider = {
        getOrCreateStoredWSId: (): string | null => null,
        getStoredWSDomain: (): string | null => null,
    };
    static loginUserDetailsProvider: { userIpAddress: string | null, userCountry: string | null } = { userIpAddress: null, userCountry: null };
    static commonHttpHeadersProvider: { getCommonHttpHeaders: () => any }
        = { getCommonHttpHeaders: () => { return {}; } };
    static authTokenProvider: { getToken: () => string | null } = { getToken: () => null };

    /**
     * API_ERROR seam — the axios twin of LightHttp's. Registered by
     * MetricsService's constructor; see utils/api-error-report.ts for why this
     * cannot be a DI edge (MetricsService injects HttpClient).
     */
    static apiErrorSink: ApiErrorSink | null = null;

    constructor(
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(EnvironmentService) private env: EnvironmentService,
        @inject(DOMStorage) private domStorage: DOMStorage,
    ) {
    }

    get<T>(url: string, options?: HttpRequestOptions) {
        let config: AxiosRequestConfig = this.convertHttpRequestOptionsToAxiosRequestConfig(url, options)
        return this.withApiErrorReporting(url, 'GET', axios.get(url, config))
    }

    /**
     * API_ERROR reporting for the axios client — the equivalent of LightHttp's
     * catchError site, applied once per verb so no call path can silently skip
     * it. The rejection is re-thrown untouched: the metric rides along, it never
     * changes what the caller sees.
     */
    private withApiErrorReporting<R>(url: string, method: string, promise: Promise<R>): Promise<R> {
        const startedAt = Date.now();
        return promise.catch((error) => {
            reportApiError(HttpClient.apiErrorSink, url, method, error, Date.now() - startedAt);
            throw error;
        });
    }
    request<T>(url: string, method: "GET" | "POST" | "PUT" | "DELETE" | "HEAD" | "PATCH", body: HttpRequestBody,
        options?: HttpRequestOptions) {
        switch (method) {
            case 'GET':
                return this.get(url, options)
            case 'POST':
                return this.post(url, body, options)
            case 'PUT':
                return this.put(url, body, options)
            case 'PATCH':
                return this.patch(url, body, options)
            case 'DELETE':
                return this.delete(url, options)
            case 'HEAD':
                throw Error("Not supported")
        }
    }
    post<T>(url: string, body: HttpRequestBody, options?: HttpRequestOptions) {
        let config: AxiosRequestConfig = this.convertHttpRequestOptionsToAxiosRequestConfig(url, options)
        return this.withApiErrorReporting(url, 'POST', axios.post(url, body, config))
    }
    put<T>(url: string, body: HttpRequestBody, options?: HttpRequestOptions) {
        let config: AxiosRequestConfig = this.convertHttpRequestOptionsToAxiosRequestConfig(url, options)
        return this.withApiErrorReporting(url, 'PUT', axios.put(url, body, config))
    }
    patch<T>(url: string, body: HttpRequestBody, options?: HttpRequestOptions) {
        let config: AxiosRequestConfig = this.convertHttpRequestOptionsToAxiosRequestConfig(url, options)
        return this.withApiErrorReporting(url, 'PATCH', axios.patch(url, body, config))
    }
    delete<T>(url: string, options?: HttpRequestOptions) {
        let config: AxiosRequestConfig = this.convertHttpRequestOptionsToAxiosRequestConfig(url, options)
        return this.withApiErrorReporting(url, 'DELETE', axios.delete(url, config))
    }

    private convertHttpRequestOptionsToAxiosRequestConfig(url: string, options?: HttpRequestOptions) {
        let config: AxiosRequestConfig = {}
        const commonHeaders = this.getCommonHeaders(url);
        if (options) {
            config.headers = Object.assign({}, commonHeaders, options.headers);
            config.withCredentials = options.withCredentials;
            config.params = options.params;
            config.responseType = options.responseType;
            config.timeout = options.timeout
        } else {
            config.headers = commonHeaders;
        }
        return config;
    }


    private convertToProgressEvent(event: AjaxResponse<any>): HttpProgressEvent {
        if (event.type === 'upload_progress' || event.type === 'download_progress') {
            return {
                event: {
                    loaded: event.loaded,
                    total: event.total,
                },
                type: event.type === 'upload_progress' ? 'upload_progress' : 'download_progress',
            }
        }

        return {
            event,
            type: event.type,
        };
    }

    /**
     * 2026-09-05 (Mongo write-stall incident, MONGO-STALL-PLAN-v5.2.md §4): this
     * converter used to throw away the status, the body AND `Retry-After` on
     * every branch — a caller could not tell a 503 "come back in 30 s" from a
     * 500. The wrappers now carry `status`, `response` and `retryAfterSeconds`,
     * parsed by the same `utils/retry-after` reader the schedule path
     * uses, so both HTTP clients answer the retry policy identically. Nothing
     * that already worked changes: the fields were previously undefined.
     */
    private convertErrorResponse(error: AjaxError, options?: { requestUrl: string }) {
        const retryAfterSeconds = readRetryAfterSeconds(error as any);
        if (error.status == 0) {
            let url = get(error || {}, 'xhr.responseURL', options?.requestUrl);
            if ((!url || url.length == 0) && options?.requestUrl) {
                url = options?.requestUrl;
            }
            const transport = new UnknownHTTPError(`Unable to reach the server. Possible causes could be server is offline, your connection to the server is blocked or you are offline. ${url}`);
            transport.status = 0;
            return transport;
        }

        const status = parseInt(error.status / 100 + '');
        if (status === 4) {
            const clientError = new ClientError(this.getErrorMessage(error, options), error.response, error.status);
            if (retryAfterSeconds !== undefined) clientError.retryAfterSeconds = retryAfterSeconds;
            return clientError;
        } else if (status === 5) {
            const serviceError = new ServiceError(this.getErrorMessage(error, options), error.status);
            serviceError.response = error.response;
            if (retryAfterSeconds !== undefined) serviceError.retryAfterSeconds = retryAfterSeconds;
            return serviceError;
        } else {
            const unknown = new UnknownHTTPError(this.getErrorMessage(error, options));
            unknown.status = error.status;
            if (retryAfterSeconds !== undefined) unknown.retryAfterSeconds = retryAfterSeconds;
            return unknown;
        }
    }

    private getErrorMessage(error: AjaxError, options?: { requestUrl?: string }): string {
        let message = get(error, 'response.message');
        const url = get(error, 'xhr.responseURL');
        if (message) {
            return message;
        }
        if (error.status || error.status === 0) {
            if (error.response) {
                return `${error.status} - ${error.response}`;
            }
            if (url) {
                return `${error.status} - unknow error while connecting to ${url}`;
            }
            return `${error.status} - unknow error`;
        }
        var resolvedUrl = url || options?.requestUrl;
        if (resolvedUrl) {
            return `Unknow error while connecting to ${resolvedUrl}`;
        }
        return `Unknow error`;
    }

    private getCommonHeaders(url: string) {
        let headers: any = {};
        const token = HttpClient.authTokenProvider.getToken();
        if (!UNAUTHENTICATED_URLS.find(regex => regex.test(url))) {
            headers = HttpClient.commonHttpHeadersProvider.getCommonHttpHeaders();
            // IMPORTANT: Need to add GCP, blob://, sockjs-node to skip url list
            headers = Object.assign(
                {},
                headers,
                {
                    /** API Domain name.
                     * //TODO please rename sitename to apiEndpointDomain
                     */
                    sitename: `${this.apiConfig.apiDomainName}`,
                    /** Domain name of the requesting website  */
                    domainname: `${this.env.domainname}`,
                    ...(HttpClient.loginUserDetailsProvider.userIpAddress ? { 'ip-address': HttpClient.loginUserDetailsProvider.userIpAddress } : null),
                    ...(HttpClient.loginUserDetailsProvider.userCountry ? { 'country': HttpClient.loginUserDetailsProvider.userCountry } : null),
                    wsId: `${HttpClient.wsDataProvider.getOrCreateStoredWSId()}`,
                    wsDomain: `${HttpClient.wsDataProvider.getStoredWSDomain()}`,
                }
            );

            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            // X-Request-Id, flag-gated — null (and so a no-op) by default.
            const requestId = requestIdHeader();
            if (requestId) {
                Object.assign(headers, requestId);
            }
        }
        return headers;
    }

    public static registerWSDetailProvider(provider: {
        getOrCreateStoredWSId: () => string | null,
        getStoredWSDomain: () => string | null,
    }) {
        HttpClient.wsDataProvider = provider;
    }

    public static registerLoginUserDetailsProvider(provider: { userIpAddress: string, userCountry: string }) {
        HttpClient.loginUserDetailsProvider = provider;
    }

    public static registerCommonHttpHeadersProvider(provider: { getCommonHttpHeaders: () => any }) {
        HttpClient.commonHttpHeadersProvider = provider;
    }

    public static registerAuthTokenProvider(provider: { getToken: () => string }) {
        HttpClient.authTokenProvider = provider;
    }

    public static registerApiErrorSink(sink: ApiErrorSink) {
        HttpClient.apiErrorSink = sink;
    }
}
